import { useMemo, useState, type FormEvent } from 'react';
import {
  IconAdjustments,
  IconCoins,
  IconCrown,
  IconNotes,
  IconSearch,
  IconStar,
} from '@tabler/icons-react';
import {
  adjustAdminOrganizationCredits,
  getAdminOrganizations,
  updateAdminOrganizationQuota,
  type AdminOrganizationItem,
} from '../api/endpoints';
import { PageHeader } from '../components/AppShell';
import { EmptyState, ErrorNotice, LoadingRows } from '../components/Feedback';
import { Mono } from '../components/Pills';
import { Badge, Button, Card, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input } from '../components/ui';
import { formatRelative } from '../lib/format';
import { useAuth, useResource, useToast } from '../state';

export function AdminCustomersPage(): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const toast = useToast();

  const orgsResource = useResource(() => getAdminOrganizations(), [], {
    enabled: isAdmin,
    live: ['run.created', 'run.finished'],
  });

  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState<string>('all');

  // Modals state
  const [selectedOrg, setSelectedOrg] = useState<AdminOrganizationItem | null>(null);
  const [isAdjustCreditsOpen, setIsAdjustCreditsOpen] = useState(false);
  const [isEditQuotaOpen, setIsEditQuotaOpen] = useState(false);

  // Adjust credits form
  const [creditDelta, setCreditDelta] = useState<number>(1000);
  const [creditReason, setCreditReason] = useState<string>('Behind-the-scenes agreement credit grant');
  const [isSubmittingCredit, setIsSubmittingCredit] = useState(false);

  // Edit quota form
  const [editPlan, setEditPlan] = useState<string>('custom');
  const [customMonthlyCredits, setCustomMonthlyCredits] = useState<number | ''>(5000);
  const [customConcurrency, setCustomConcurrency] = useState<number | ''>('');
  const [customRateLimitRpm, setCustomRateLimitRpm] = useState<number | ''>('');
  const [customEnvironments, setCustomEnvironments] = useState<number | ''>('');
  const [customRunHistoryDays, setCustomRunHistoryDays] = useState<number | ''>('');
  const [priority, setPriority] = useState<'normal' | 'high' | 'vip'>('normal');
  const [isExemptFromBilling, setIsExemptFromBilling] = useState<boolean>(false);
  const [agreementNotes, setAgreementNotes] = useState<string>('');
  const [contractExpiresAt, setContractExpiresAt] = useState<string>('');
  const [isSubmittingQuota, setIsSubmittingQuota] = useState(false);

  const orgs = orgsResource.data?.organizations ?? [];

  const filteredOrgs = useMemo(() => {
    return orgs.filter((org) => {
      const matchSearch =
        org.name.toLowerCase().includes(search.toLowerCase()) ||
        org.id.toLowerCase().includes(search.toLowerCase()) ||
        org.ownerEmail.toLowerCase().includes(search.toLowerCase()) ||
        (org.overrides?.agreementNotes ?? '').toLowerCase().includes(search.toLowerCase());

      if (!matchSearch) return false;
      if (planFilter !== 'all' && org.plan !== planFilter) return false;
      return true;
    });
  }, [orgs, search, planFilter]);

  const openAdjustCreditsModal = (org: AdminOrganizationItem) => {
    setSelectedOrg(org);
    setCreditDelta(1000);
    setCreditReason(`Agreement grant for ${org.name}`);
    setIsAdjustCreditsOpen(true);
  };

  const handleAdjustCredits = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedOrg) return;
    setIsSubmittingCredit(true);
    try {
      await adjustAdminOrganizationCredits(selectedOrg.id, {
        delta: creditDelta,
        reason: creditReason,
      });
      toast.success(`Updated credits for ${selectedOrg.name} by ${creditDelta > 0 ? '+' : ''}${creditDelta}`);
      setIsAdjustCreditsOpen(false);
      orgsResource.reload();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to adjust credits');
    } finally {
      setIsSubmittingCredit(false);
    }
  };

  const openEditQuotaModal = (org: AdminOrganizationItem) => {
    setSelectedOrg(org);
    setEditPlan(org.plan);
    setCustomMonthlyCredits(org.overrides?.customMonthlyCredits ?? '');
    setCustomConcurrency(org.overrides?.customConcurrency ?? '');
    setCustomRateLimitRpm(org.overrides?.customRateLimitRpm ?? '');
    setCustomEnvironments(org.overrides?.customEnvironmentsAllowed ?? '');
    setCustomRunHistoryDays(org.overrides?.customRunHistoryDays ?? '');
    setPriority(org.overrides?.priority ?? 'normal');
    setIsExemptFromBilling(Boolean(org.overrides?.isExemptFromBilling));
    setAgreementNotes(org.overrides?.agreementNotes ?? '');
    setContractExpiresAt(org.overrides?.contractExpiresAt ?? '');
    setIsEditQuotaOpen(true);
  };

  const handleSaveQuota = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedOrg) return;
    setIsSubmittingQuota(true);
    try {
      await updateAdminOrganizationQuota(selectedOrg.id, {
        plan: editPlan,
        overrides: {
          customMonthlyCredits: customMonthlyCredits !== '' ? Number(customMonthlyCredits) : undefined,
          customConcurrency: customConcurrency !== '' ? Number(customConcurrency) : undefined,
          customRateLimitRpm: customRateLimitRpm !== '' ? Number(customRateLimitRpm) : undefined,
          customEnvironmentsAllowed: customEnvironments !== '' ? Number(customEnvironments) : undefined,
          customRunHistoryDays: customRunHistoryDays !== '' ? Number(customRunHistoryDays) : undefined,
          priority,
          isExemptFromBilling,
          agreementNotes: agreementNotes.trim() || undefined,
          contractExpiresAt: contractExpiresAt.trim() || undefined,
        },
      });
      toast.success(`Updated quota and overrides for ${selectedOrg.name}`);
      setIsEditQuotaOpen(false);
      orgsResource.reload();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update quota');
    } finally {
      setIsSubmittingQuota(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="page">
        <PageHeader title="Access Denied" subtitle="Super-admin authorization required." />
        <ErrorNotice error={new Error('Only operators with the admin role can access customer management.')} />
      </div>
    );
  }

  return (
    <div className="page admin-customers-page">
      <PageHeader
        title="Customer & Custom Quota Management"
        subtitle="Manage custom B2B agreements, individual credit allowances, queue priorities, and billing exemptions."
      />

      <div className="admin-customers__toolbar mb-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-1 items-center gap-3">
          <div className="relative max-w-sm flex-1">
            <IconSearch size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by organization, ID, email, or notes..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          <select
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm"
          >
            <option value="all">All Plans</option>
            <option value="custom">Custom Deal</option>
            <option value="enterprise">Enterprise</option>
            <option value="pro">Pro</option>
            <option value="standard">Standard</option>
            <option value="starter">Starter</option>
            <option value="free">Free</option>
          </select>
        </div>

        <div className="text-sm text-muted-foreground">
          Showing <strong>{filteredOrgs.length}</strong> of {orgs.length} organizations
        </div>
      </div>

      {orgsResource.loading && !orgsResource.data ? (
        <LoadingRows rows={5} label="Loading organizations..." />
      ) : filteredOrgs.length === 0 ? (
        <EmptyState title="No organizations found">
          <p>Try adjusting your search criteria or plan filter.</p>
        </EmptyState>
      ) : (
        <div className="space-y-4">
          {filteredOrgs.map((org) => {
            const hasOverrides =
              org.overrides &&
              (org.overrides.customMonthlyCredits !== undefined ||
                org.overrides.customConcurrency !== undefined ||
                org.overrides.customRateLimitRpm !== undefined ||
                org.overrides.isExemptFromBilling ||
                org.overrides.priority === 'vip' ||
                org.overrides.priority === 'high' ||
                Boolean(org.overrides.agreementNotes));

            return (
              <Card key={org.id} className="p-5 transition-shadow hover:shadow-md">
                <div className="flex flex-col justify-between gap-4 md:flex-row md:items-start">
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-lg font-semibold tracking-tight">{org.name}</h3>
                      <Mono>{org.id}</Mono>
                      <Badge
                        variant={
                          org.plan === 'custom'
                            ? 'default'
                            : org.plan === 'enterprise'
                            ? 'secondary'
                            : 'outline'
                        }
                      >
                        {org.plan === 'custom' ? 'Custom Deal' : org.plan.toUpperCase()}
                      </Badge>
                      {org.overrides?.priority === 'vip' ? (
                        <Badge variant="destructive" className="flex items-center gap-1">
                          <IconCrown size={12} /> VIP Priority
                        </Badge>
                      ) : org.overrides?.priority === 'high' ? (
                        <Badge variant="secondary" className="flex items-center gap-1">
                          <IconStar size={12} /> High Priority
                        </Badge>
                      ) : null}
                      {org.overrides?.isExemptFromBilling ? (
                        <Badge variant="secondary" className="bg-emerald-500/10 text-emerald-600">
                          Billing Exempt
                        </Badge>
                      ) : null}
                    </div>

                    <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                      <span>Owner: <strong>{org.ownerEmail}</strong></span>
                      <span>Members: {org.membersCount}</span>
                      <span>Projects: {org.projectsCount}</span>
                      <span>Tests: {org.testsCount}</span>
                      <span>Runs: {org.runsCount}</span>
                      <span>Joined: {formatRelative(org.createdAt)}</span>
                    </div>

                    {org.overrides?.agreementNotes ? (
                      <div className="mt-2 flex items-start gap-2 rounded-md bg-muted/50 p-2.5 text-xs">
                        <IconNotes size={16} className="mt-0.5 shrink-0 text-amber-500" />
                        <div>
                          <span className="font-semibold text-foreground">Agreement Notes: </span>
                          <span className="text-muted-foreground">{org.overrides.agreementNotes}</span>
                          {org.overrides.contractExpiresAt ? (
                            <span className="ml-2 font-medium text-amber-600">
                              (Expires: {org.overrides.contractExpiresAt})
                            </span>
                          ) : null}
                        </div>
                      </div>
                    ) : null}

                    {hasOverrides ? (
                      <div className="mt-2 flex flex-wrap gap-2 text-xs">
                        {org.overrides.customMonthlyCredits !== undefined ? (
                          <span className="rounded bg-primary/10 px-2 py-0.5 font-medium text-primary">
                            Monthly: {org.overrides.customMonthlyCredits.toLocaleString()} cr
                          </span>
                        ) : null}
                        {org.overrides.customConcurrency !== undefined ? (
                          <span className="rounded bg-muted px-2 py-0.5 text-muted-foreground">
                            Concurrency: {org.overrides.customConcurrency} slots
                          </span>
                        ) : null}
                        {org.overrides.customRateLimitRpm !== undefined ? (
                          <span className="rounded bg-muted px-2 py-0.5 text-muted-foreground">
                            Rate Limit: {org.overrides.customRateLimitRpm} rpm
                          </span>
                        ) : null}
                        {org.overrides.customEnvironmentsAllowed !== undefined ? (
                          <span className="rounded bg-muted px-2 py-0.5 text-muted-foreground">
                            Envs: {org.overrides.customEnvironmentsAllowed === -1 ? 'Unlimited' : org.overrides.customEnvironmentsAllowed}
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                  </div>

                  <div className="flex shrink-0 flex-col items-end gap-3">
                    <div className="text-right">
                      <div className="text-2xl font-bold tracking-tight">
                        {org.creditsRemaining.toFixed(1)}{' '}
                        <span className="text-xs font-normal text-muted-foreground">cr</span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Spent this month: {org.spentThisMonth.toFixed(1)} cr
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openAdjustCreditsModal(org)}
                        className="flex items-center gap-1.5"
                      >
                        <IconCoins size={14} /> Adjust Credits
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => openEditQuotaModal(org)}
                        className="flex items-center gap-1.5"
                      >
                        <IconAdjustments size={14} /> Edit Quota & Deal
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Adjust Credits Dialog */}
      <Dialog open={isAdjustCreditsOpen} onOpenChange={setIsAdjustCreditsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Adjust Organization Credits</DialogTitle>
            <DialogDescription>
              Grant or deduct credits for <strong>{selectedOrg?.name}</strong>. Transactions are permanently audited in the credit ledger.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleAdjustCredits} className="space-y-4 py-2">
            <div>
              <label className="mb-1.5 block text-xs font-medium">Credit Delta (+ to grant, - to deduct)</label>
              <Input
                type="number"
                step="1"
                required
                value={creditDelta}
                onChange={(e) => setCreditDelta(Number(e.target.value))}
                placeholder="e.g. 5000 or -500"
              />
              <span className="mt-1 block text-xs text-muted-foreground">
                Current balance: {selectedOrg?.creditsRemaining.toFixed(1)} cr &rarr; Result:{' '}
                {((selectedOrg?.creditsRemaining ?? 0) + (creditDelta || 0)).toFixed(1)} cr
              </span>
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-medium">Reason & Audit Note</label>
              <Input
                type="text"
                required
                value={creditReason}
                onChange={(e) => setCreditReason(e.target.value)}
                placeholder="e.g. Offline contract invoice #402, Goodwill bonus"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setIsAdjustCreditsOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmittingCredit}>
                {isSubmittingCredit ? 'Saving...' : 'Apply Credit Adjustment'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Quota & Overrides Dialog */}
      <Dialog open={isEditQuotaOpen} onOpenChange={setIsEditQuotaOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Edit Quota & Agreement: {selectedOrg?.name}</DialogTitle>
            <DialogDescription>
              Configure custom negotiated terms, monthly credit rollover caps, concurrency priority, and rate limits.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSaveQuota} className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1.5 block text-xs font-medium">Plan Tier</label>
                <select
                  value={editPlan}
                  onChange={(e) => setEditPlan(e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm"
                >
                  <option value="custom">Custom Deal (Negotiated)</option>
                  <option value="enterprise">Enterprise</option>
                  <option value="pro">Pro</option>
                  <option value="standard">Standard</option>
                  <option value="starter">Starter</option>
                  <option value="free">Free</option>
                </select>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium">Queue Execution Priority</label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as any)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm"
                >
                  <option value="normal">Normal (Standard FIFO)</option>
                  <option value="high">High (Priority Queue)</option>
                  <option value="vip">VIP (Preempts standard runs)</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1.5 block text-xs font-medium">Custom Monthly Credits</label>
                <Input
                  type="number"
                  min="0"
                  value={customMonthlyCredits}
                  onChange={(e) => setCustomMonthlyCredits(e.target.value === '' ? '' : Number(e.target.value))}
                  placeholder="e.g. 10000 (leaves blank for tier default)"
                />
                <span className="mt-1 block text-xs text-muted-foreground">
                  Refreshed every 30 days automatically
                </span>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium">Custom Concurrency Limit</label>
                <Input
                  type="number"
                  min="1"
                  value={customConcurrency}
                  onChange={(e) => setCustomConcurrency(e.target.value === '' ? '' : Number(e.target.value))}
                  placeholder="e.g. 5 (leaves blank for global default)"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1.5 block text-xs font-medium">Custom Rate Limit (req/min)</label>
                <Input
                  type="number"
                  min="1"
                  value={customRateLimitRpm}
                  onChange={(e) => setCustomRateLimitRpm(e.target.value === '' ? '' : Number(e.target.value))}
                  placeholder="e.g. 600 (default 120)"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium">Environments Allowed</label>
                <Input
                  type="number"
                  value={customEnvironments}
                  onChange={(e) => setCustomEnvironments(e.target.value === '' ? '' : Number(e.target.value))}
                  placeholder="e.g. 10 (-1 for unlimited)"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 rounded-md border border-input p-3">
              <input
                type="checkbox"
                id="exemptCheck"
                checked={isExemptFromBilling}
                onChange={(e) => setIsExemptFromBilling(e.target.checked)}
                className="h-4 w-4 rounded border-input"
              />
              <label htmlFor="exemptCheck" className="text-xs font-medium leading-none cursor-pointer">
                Exempt from billing / past-due blocking (Offline B2B contract invoice)
              </label>
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-medium">Behind-the-scenes Agreement Notes</label>
              <textarea
                rows={2}
                value={agreementNotes}
                onChange={(e) => setAgreementNotes(e.target.value)}
                placeholder="e.g. Deal closed via WhatsApp: Transfer Bank BCA Rp 10.000.000/bln, dedicated support"
                className="w-full rounded-md border border-input bg-background p-2.5 text-xs shadow-sm"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-medium">Contract Expiration Date</label>
              <Input
                type="date"
                value={contractExpiresAt}
                onChange={(e) => setContractExpiresAt(e.target.value)}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setIsEditQuotaOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmittingQuota}>
                {isSubmittingQuota ? 'Saving...' : 'Save Quota & Agreement'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
